import { AffiliateSettingsConstants } from "@@types/IAffiliate";
import { AvailableLanguages } from "@@types/IGenerics";
import BR from "@assets/flags/BR.svg";
import US from "@assets/flags/US.svg";
import { Image } from "@components/common/ui/Image";
import { useToastContext } from "@components/common/ui/Toast/Context";
import { useAffiliateData } from "@hooks/affiliates/affiliate/useAffiliateData";
import CopyToClipboard from "react-copy-to-clipboard";
import { Copy } from "react-feather";
import { Col, Row } from "react-grid-system";
import * as RootS from "../styles";
import * as S from "./styles";

export default function InformationCard() {
  const toast = useToastContext();
  const { data } = useAffiliateData();
  const settings = data?.productAffiliateSettings;
  const product = data?.product;
  const languageText = (language: string | undefined) => {
    switch (language) {
      case AvailableLanguages["pt-BR"]:
        return (
          <>
            <Image lightIcon={BR} darkIcon={BR} alt={language} height="16" /> Português
          </>
        );
      case AvailableLanguages["en-US"]:
        return (
          <>
            <Image darkIcon={US} lightIcon={US} alt={language} height="16" /> Inglês
          </>
        );
      default:
        return null;
    }
  };

  const fixedChargesText = (fixedCharges?: number) => {
    if (!fixedCharges) return null;
    if (fixedCharges === 1) return ` | Número fixo de cobrança: ${fixedCharges}`;
    return ` | Número fixo de cobranças: ${fixedCharges}`;
  };

  const handleCopyCode = () => {
    toast({ type: "info", title: "Link copiado" });
  };

  if (!product) return <></>;
  return (
    <RootS.ContentCard justifyContent="start" alignItems="start">
      <RootS.SubTitle dark margin="small">
        Informações gerais
      </RootS.SubTitle>
      <RootS.Text>{product?.description}</RootS.Text>
      {settings ? (
        <S.ProductInfoTable>
          <Row gutterWidth={24}>
            {settings.userProducerName != "RUYTER DE MENDONCA POUBEL" && (
              <Col sm={12} md={6}>
                <S.ProductInfoTableItem>
                  <S.ProductInfoTitle bold>Produtor</S.ProductInfoTitle>
                  <S.ProductInfoText>{settings.userProducerName}</S.ProductInfoText>
                </S.ProductInfoTableItem>
              </Col>
            )}
            <Col sm={12} md={6}>
              <S.ProductInfoTableItem>
                <S.ProductInfoTitle bold>Tipo</S.ProductInfoTitle>
                <S.ProductInfoText>{product?.format}</S.ProductInfoText>
              </S.ProductInfoTableItem>
            </Col>
          </Row>
          <Row gutterWidth={24}>
            <Col sm={12} md={6}>
              <S.ProductInfoTableItem>
                <S.ProductInfoTitle bold>Página de vendas</S.ProductInfoTitle>
                <S.ProductInfoText>
                  <S.SuportLink path={settings.supportPage} target="_blank">
                    {settings.supportPage}
                  </S.SuportLink>

                  <CopyToClipboard text={settings.supportPage} onCopy={handleCopyCode}>
                    <S.CopyIconWrapper>
                      <Copy />
                    </S.CopyIconWrapper>
                  </CopyToClipboard>
                </S.ProductInfoText>
              </S.ProductInfoTableItem>
            </Col>
            <Col sm={12} md={6}>
              <S.ProductInfoTableItem>
                <S.ProductInfoTitle bold>Comissão estendida</S.ProductInfoTitle>
                <S.ProductInfoText>
                  {settings.extendedCommissionEnabled
                    ? "Também será aplicada em cross sell, upsell, downsell e order bump"
                    : "Não será aplicada em cross sell, upsell, downsell e order bump"}
                </S.ProductInfoText>
              </S.ProductInfoTableItem>
            </Col>
          </Row>
          <Row gutterWidth={24}>
            <Col sm={12} md={6}>
              <S.ProductInfoTableItem>
                <S.ProductInfoTitle bold>Idioma</S.ProductInfoTitle>
                <S.ProductInfoText>{languageText(settings.productLanguage)}</S.ProductInfoText>
              </S.ProductInfoTableItem>
            </Col>
            <Col sm={12} md={6}>
              <S.ProductInfoTableItem>
                <S.ProductInfoTitle bold>Aprovação</S.ProductInfoTitle>
                <S.ProductInfoText>
                  {settings.automaticAffiliateEnabled ? "Imediata" : "Mediante moderação do produtor"}
                </S.ProductInfoText>
              </S.ProductInfoTableItem>
            </Col>
          </Row>
          <Row gutterWidth={24}>
            <Col sm={12} md={6}>
              <S.ProductInfoTableItem>
                <S.ProductInfoTitle bold>Comissão</S.ProductInfoTitle>
                <S.ProductInfoText>
                  {settings.oneTimeCommission}% em ofertas de preço único <br />
                  {settings.recurrenceCommission}% em ofertas recorrentes
                  {settings.recurrenceChargeTypes === AffiliateSettingsConstants.RecurrenceChargeTypes.ALL_CHARGES &&
                    " | Todas as cobranças"}
                  {settings.recurrenceChargeTypes === AffiliateSettingsConstants.RecurrenceChargeTypes.FIRST_CHARGE &&
                    " | Primeira cobrança"}
                  {settings.recurrenceChargeTypes === AffiliateSettingsConstants.RecurrenceChargeTypes.FIXED_NUMBER &&
                    fixedChargesText(settings.recurrenceFixedCharges)}
                </S.ProductInfoText>
              </S.ProductInfoTableItem>
            </Col>
            <Col sm={12} md={6}></Col>
          </Row>
        </S.ProductInfoTable>
      ) : (
        <S.ProductInfoTable>
          <Row gutterWidth={24}>
            <Col sm={12} md={6}>
              <S.ProductInfoTableItem>
                <S.ProductInfoTitle bold>Produtor</S.ProductInfoTitle>
                <S.ProductInfoText>{product.producerName}</S.ProductInfoText>
              </S.ProductInfoTableItem>
            </Col>
          </Row>
          <Row>
            <Col sm={12} md={6}>
              <S.ProductInfoTableItem>
                <S.ProductInfoTitle bold>E-mail para suporte</S.ProductInfoTitle>
                <S.ProductInfoText>{product.producerEmail}</S.ProductInfoText>
              </S.ProductInfoTableItem>
            </Col>
          </Row>
          <Row>
            <Col sm={12} md={6}>
              <S.ProductInfoTableItem>
                <S.ProductInfoTitle bold>Página de vendas</S.ProductInfoTitle>
                <S.ProductInfoText>
                  <S.SuportLink path={product.productPage} target="_blank">
                    {product.productPage}
                  </S.SuportLink>
                  <CopyToClipboard text={product.productPage} onCopy={handleCopyCode}>
                    <S.CopyIconWrapper>
                      <Copy />
                    </S.CopyIconWrapper>
                  </CopyToClipboard>
                </S.ProductInfoText>
              </S.ProductInfoTableItem>
            </Col>
          </Row>
        </S.ProductInfoTable>
      )}
    </RootS.ContentCard>
  );
}
